/** @jsxImportSource @emotion/react */
import React from "react";
import * as lodash from "lodash";
import {
  Input,
  Space,
  Switch,
  Table,
  Image,
  Button,
  Popover,
  Checkbox,
  Radio,
  RadioChangeEvent,
  TableColumnsType,
  InputNumber,
  Form,
  notification,
  Tooltip,
  Typography,
  Row,
  Col,
} from "antd";
import {
  CloseCircleOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  FileImageOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { LayoutContent } from "../../../components";
import {
  MaterialUsecase,
  useMaterialDispatch,
  useMaterialState,
} from "../../../usecases/Material";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { MaterialColumns } from "../../../usecases/Material/usecase.d";
import * as Styles from "./styles";
import { TenantUsecase, useTenantDispatch } from "../../../usecases/Tenant";
import { ColumnFilterItem, TableRowSelection } from "antd/lib/table/interface";
import { GetListParams } from "../../../services/Material/material.repository.d";
import { CheckboxOptionType, CheckboxValueType } from "antd/lib/checkbox/Group";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { GetMaterialDto } from "../../../services/Material/dto/get.dto";
import { Images } from "../../../common/images";
import dayjs from "dayjs";
import { useAuthState } from "../../../usecases/Auth";
import { useSettingState } from "../../../usecases/Setting";

const titleKeys = {
  materialThumbnailUrl: "Thumbnail",
  materialKey: "Key",
  name: "Name",
  materialCategories: "Category",
  materialUnitPrice: "Unit Price",
  materialRooms: "Room",
  materialSurfaces: "Surface",
  materialManufacturer: "Manufacturer",
  homeNames: "Homes",
  updatedByFullName: "Modified By",
  updatedAt: "Date Modified",
  isActive: "Status",
};

interface HeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
}

interface BodyCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
  editing: boolean;
  inputType: InputType;
  dataIndex: string;
  record: GetMaterialDto;
}

interface DragIndexState {
  active: UniqueIdentifier;
  over: UniqueIdentifier | undefined;
  direction?: "left" | "right";
}

const DragIndexContext = React.createContext<DragIndexState>({
  active: -1,
  over: -1,
});

const dragActiveStyle = (dragState: DragIndexState, id: string) => {
  const { active, over } = dragState;
  // drag active style
  let style: React.CSSProperties = {};
  if (active && active === id) {
    style = { backgroundColor: "#E7E7E7", opacity: 0.7 };
  } else if (over && id === over && active !== over) {
    style = { borderInlineStart: "1px dashed gray" };
  }
  return style;
};

const TableHeaderCell: React.FC<HeaderCellProps> = (props) => {
  const dragState = React.useContext(DragIndexContext);
  // eslint-disable-next-line react/prop-types
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    // eslint-disable-next-line react/prop-types
    id: props.id,
  });
  const style: React.CSSProperties = {
    // eslint-disable-next-line react/prop-types
    ...props.style,
    cursor: "move",
    ...(isDragging
      ? { position: "relative", zIndex: 9999, userSelect: "none" }
      : {}),
    // eslint-disable-next-line react/prop-types
    ...dragActiveStyle(dragState, props.id),
  };
  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

const TableBodyCell: React.FC<BodyCellProps> = (props: BodyCellProps) => {
  const dragState = React.useContext<DragIndexState>(DragIndexContext);
  const style = {
    ...props.style,
    ...dragActiveStyle(dragState, props.id),
  };
  const { dataIndex, record } = props;

  const inputType = React.useCallback((type: InputType) => {
    switch (type) {
      case InputType.Text:
        return <Input />;
      case InputType.Number:
        return <InputNumber />;
      case InputType.Switch:
        return <Switch />;
      default:
        return <></>;
    }
  }, []);

  const valuePropName = React.useCallback((type: InputType) => {
    switch (type) {
      case InputType.Text:
      case InputType.Number:
        return "value";
      case InputType.Switch:
        return "checked";
      default:
        return "";
    }
  }, []);

  const formName = `${dataIndex}_${record?.materialKey}`;
  if (props.editing === true) {
    return (
      <td {...props} style={style}>
        <Form.Item
          name={formName}
          css={Styles.formInputStyle}
          valuePropName={valuePropName(props.inputType)}
        >
          {inputType(props.inputType)}
        </Form.Item>
      </td>
    );
  }
  return (
    <td
      {...props}
      // eslint-disable-next-line react/prop-types
      style={style}
    />
  );
};

enum MaterialMode {
  View,
  Edit,
}

enum InputType {
  Text,
  Number,
  Switch,
}

const columnIds = {
  materialThumbnailUrl: "0",
  materialKey: "1",
  name: "2",
  materialCategories: "3",
  materialUnitPrice: "4",
  materialRooms: "5",
  materialSurfaces: "6",
  materialManufacturer: "7",
  homeNames: "8",
  updatedByFullName: "9",
  updatedAt: "10",
  isActive: "11",
};

interface MaterialComponentProps {}

const MaterialComponent: React.FC<MaterialComponentProps> = () => {
  const [form] = Form.useForm();
  const materialState = useMaterialState();
  const materialDispatch = useMaterialDispatch();
  const tenantDispatch = useTenantDispatch();
  // const [api, contextHolder] = notification.useNotification();
  const [editKeys, setEditKeys] = React.useState<React.Key[]>([]);

  const [dragIndex, setDragIndex] = React.useState<DragIndexState>({
    active: -1,
    over: -1,
  });

  const authState = useAuthState();
  const settingState = useSettingState();

  const [mode, setMode] = React.useState<MaterialMode>(MaterialMode.View);
  const [checkedRowKeys, setCheckedRowKeys] = React.useState<React.Key[]>([]);

  const [displayColumns, setDisplayColumns] = React.useState<MaterialColumns>(
    {} as MaterialColumns,
  );
  const [listParams, setListParams] = React.useState<GetListParams>({});

  React.useEffect(() => {
    if (settingState) {
      if (listParams.tenantId !== settingState.tenantInfo?.tenantId) {
        setListParams({
          ...listParams,
          tenantId: settingState.tenantInfo?.tenantId,
        });
      }
    }
  }, [listParams, settingState]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setColumns((prevState) => {
        const activeIndex = prevState.findIndex(
          (i) => columnIds[i.key] === active?.id,
        );
        const overIndex = prevState.findIndex(
          (i) => columnIds[i.key] === over?.id,
        );
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    const activeIndex = columns.findIndex(
      (i) => columnIds[i.key] === active.id,
    );
    const overIndex = columns.findIndex((i) => columnIds[i.key] === over?.id);
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? "right" : "left",
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onKeywordChangeHandle = React.useCallback(
    lodash.debounce((event) => {
      setListParams({
        ...listParams,
        keyword: event.target.value,
      });
    }, 300),
    [],
  );

  const sorterMaterialThumbnailUrl = React.useCallback((a, b) => {
    if (a.materialThumbnailUrl < b.materialThumbnailUrl) return -1;
    if (a.materialThumbnailUrl > b.materialThumbnailUrl) return 1;
    return 0;
  }, []);

  const sorterMaterialKey = React.useCallback((a, b) => {
    if (a.materialKey < b.materialKey) return -1;
    if (a.materialKey > b.materialKey) return 1;
    return 0;
  }, []);

  const sorterMaterialName = React.useCallback((a, b) => {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  }, []);

  const sorterMaterialManufacturer = React.useCallback((a, b) => {
    if (a.materialManufacturer < b.materialManufacturer) return -1;
    if (a.materialManufacturer > b.materialManufacturer) return 1;
    return 0;
  }, []);

  const sorterCategories = React.useCallback((a, b) => {
    const sourceCategories = a.materialCategories?.join(",");
    const targetCategories = b.materialCategories?.join(",");
    if (sourceCategories < targetCategories) return -1;
    if (sourceCategories > targetCategories) return 1;
    return 0;
  }, []);

  const sorterRooms = React.useCallback((a, b) => {
    if (a.materialRooms < b.materialRooms) return -1;
    if (a.materialRooms > b.materialRooms) return 1;
    return 0;
  }, []);

  const sorterSurfaces = React.useCallback((a, b) => {
    if (a.materialSurfaces < b.materialSurfaces) return -1;
    if (a.materialSurfaces > b.materialSurfaces) return 1;
    return 0;
  }, []);

  const sorterHomeNames = React.useCallback((a, b) => {
    const sourceHomeNames = a.homeNames?.join(",") ?? "";
    const targetHomeNames = b.homeNames?.join(",") ?? "";
    if (sourceHomeNames < targetHomeNames) return -1;
    if (sourceHomeNames > targetHomeNames) return 1;
    return 0;
  }, []);

  const sorterUnitPrice = React.useCallback((a, b) => {
    return a.materialUnitPrice - b.materialUnitPrice;
  }, []);

  const sorterUpdatedByFullName = React.useCallback((a, b) => {
    if (a.updatedByFullName < b.updatedByFullName) return -1;
    if (a.updatedByFullName > b.updatedByFullName) return 1;
    return 0;
  }, []);

  const sorterUpdatedAt = React.useCallback((a, b) => {
    if (a.updatedAt < b.updatedAt) return -1;
    if (a.updatedAt > b.updatedAt) return 1;
    return 0;
  }, []);

  const sorterStatus = React.useCallback((a) => {
    return a.isActive;
  }, []);

  const onStatusFilter = React.useCallback((value, record) => {
    return record.isActive === value;
  }, []);

  const onCategoriesFilter = React.useCallback((value, record) => {
    return record.materialCategories?.some((val) => val === value);
  }, []);

  const onRoomsFilter = React.useCallback((value, record) => {
    return record.materialRooms?.some((val) => val === value);
  }, []);

  const onSurfacesFilter = React.useCallback((value, record) => {
    return record.materialSurfaces?.some((val) => val === value);
  }, []);

  const [categories, setCategories] = React.useState<ColumnFilterItem[]>([]);
  const [rooms, setRooms] = React.useState<ColumnFilterItem[]>([]);
  const [surfaces, setSurfaces] = React.useState<ColumnFilterItem[]>([]);

  const getListCategoryFilters = React.useCallback((data: GetMaterialDto[]) => {
    let result: Array<ColumnFilterItem> = [];
    data.forEach((item) => {
      item.materialCategories?.forEach((category) => {
        const filterItem: ColumnFilterItem = {
          text: category === "" ? "None" : category,
          value: category,
        };
        if (
          result.filter((item) => item.text === filterItem.text).length === 0
        ) {
          result.push(filterItem);
        }
      });
    });
    result = result.sort((a, b) =>
      a.value < b.value ? -1 : a.value > b.value ? 1 : 0,
    );
    return result;
  }, []);

  const getListRoomFilters = React.useCallback((data: GetMaterialDto[]) => {
    let result: Array<ColumnFilterItem> = [];
    data.forEach((item) => {
      item.materialRooms?.forEach((room) => {
        const filterItem: ColumnFilterItem = {
          text: room === "" ? "None" : room,
          value: room,
        };
        if (
          result.filter((item) => item.text === filterItem.text).length === 0
        ) {
          result.push(filterItem);
        }
      });
    });
    result = result.sort((a, b) =>
      a.value < b.value ? -1 : a.value > b.value ? 1 : 0,
    );
    return result;
  }, []);

  const getListSurfaceFilters = React.useCallback((data: GetMaterialDto[]) => {
    let result: Array<ColumnFilterItem> = [];
    data.forEach((item) => {
      item.materialSurfaces?.forEach((surface) => {
        const filterItem: ColumnFilterItem = {
          text: surface === "" ? "None" : surface,
          value: surface,
        };
        if (
          result.filter((item) => item.text === filterItem.text).length === 0
        ) {
          result.push(filterItem);
        }
      });
    });
    result = result.sort((a, b) =>
      a.value < b.value ? -1 : a.value > b.value ? 1 : 0,
    );
    return result;
  }, []);

  React.useEffect(() => {
    const result = getListCategoryFilters(materialState.data);
    setCategories(result);
  }, [getListCategoryFilters, materialState.data]);

  React.useEffect(() => {
    const result = getListRoomFilters(materialState.data);
    setRooms(result);
  }, [getListRoomFilters, materialState.data]);

  React.useEffect(() => {
    const result = getListSurfaceFilters(materialState.data);
    setSurfaces(result);
  }, [getListSurfaceFilters, materialState.data]);

  const displayColumnLabels = React.useCallback(() => {
    const result = [];
    for (const property in displayColumns) {
      const value = displayColumns[property] as boolean;
      const label = titleKeys[property] as string;
      result.push({
        value,
        label,
      });
    }
    return result;
  }, [displayColumns]);

  const displayColumnOptions = React.useCallback((): CheckboxOptionType[] => {
    return displayColumnLabels().map((item) => item.label);
  }, [displayColumnLabels]);

  const displayColumnValues = React.useCallback((): CheckboxValueType[] => {
    const result = [];
    for (const property in displayColumns) {
      const value = displayColumns[property] as boolean;
      const label = titleKeys[property] as string;
      if (value) {
        result.push(label);
      }
    }
    return result;
  }, [displayColumns]);

  const [thumbnailSize, setThumbnailSize] = React.useState("medium");

  const thumbnailSizeValue = React.useCallback(() => {
    if (thumbnailSize === "small") return 50;
    else if (thumbnailSize === "medium") return 100;
    return 200;
  }, [thumbnailSize]);

  const isEditing = React.useCallback(() => {
    if (mode === MaterialMode.Edit) return true;
    return false;
  }, [mode]);

  const renderThumbnailCell = React.useCallback(
    (value) => {
      if (value.materialThumbnailUrl) {
        return (
          <Image
            width={thumbnailSizeValue()}
            height={thumbnailSizeValue()}
            src={value.materialThumbnailUrl}
            fallback={Images.DEFAULT_THUMBNAIL}
            placeholder={
              <Image
                preview={false}
                src={Images.DEFAULT_THUMBNAIL}
                width={thumbnailSizeValue()}
                height={thumbnailSizeValue()}
              />
            }
          />
        );
      }
      return <></>;
    },
    [thumbnailSizeValue],
  );

  const renderTextTooltipCell = React.useCallback((value) => {
    return (
      <Tooltip title={value}>
        <Typography.Paragraph ellipsis css={Styles.singleTextLineStyle}>
          {value}
        </Typography.Paragraph>
      </Tooltip>
    );
  }, []);

  const renderArrayStringCell = React.useCallback((value) => {
    const newValue = value?.join(", ");
    return (
      <Tooltip title={newValue}>
        <Typography.Paragraph ellipsis css={Styles.singleTextLineStyle}>
          {newValue}
        </Typography.Paragraph>
      </Tooltip>
    );
  }, []);

  const renderStatusCell = React.useCallback((value) => {
    return <Switch disabled checked={value.isActive} />;
  }, []);

  const renderUpdatedAt = React.useCallback((value) => {
    return dayjs(value).format("DD/MM/YYYY");
  }, []);

  const baseColumns = React.useCallback(
    (): TableColumnsType<GetMaterialDto> => [
      {
        key: "materialThumbnailUrl",
        title: "Thumbnail",
        render: renderThumbnailCell,
        align: "left",
        sorter: sorterMaterialThumbnailUrl,
        onHeaderCell: () => ({ id: columnIds.materialThumbnailUrl }),
        onCell: () => ({ id: columnIds.materialThumbnailUrl }),
      },
      {
        key: "materialKey",
        title: "Key",
        dataIndex: "materialKey",
        align: "left",
        sorter: sorterMaterialKey,
        onHeaderCell: () => ({ id: columnIds.materialKey }),
        onCell: () => ({ id: columnIds.materialKey }),
      },
      {
        key: "name",
        title: "Name",
        dataIndex: "name",
        align: "left",
        render: renderTextTooltipCell,
        sorter: sorterMaterialName,
        onHeaderCell: () => ({ id: columnIds.name }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.name,
          record,
          dataIndex: "name",
        }),
      },
      {
        key: "materialCategories",
        title: "Category",
        dataIndex: "materialCategories",
        align: "left",
        render: renderArrayStringCell,
        sorter: sorterCategories,
        onFilter: onCategoriesFilter,
        filters: categories,
        onHeaderCell: () => ({ id: columnIds.materialCategories }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.materialCategories,
          record,
          dataIndex: "materialCategories",
        }),
      },
      {
        key: "materialUnitPrice",
        title: "Unit Price",
        dataIndex: "materialUnitPrice",
        align: "left",
        sorter: sorterUnitPrice,
        onHeaderCell: () => ({ id: columnIds.materialUnitPrice }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.materialUnitPrice,
          record,
          dataIndex: "materialUnitPrice",
        }),
      },
      {
        key: "materialRooms",
        title: "Room",
        dataIndex: "materialRooms",
        align: "left",
        render: renderArrayStringCell,
        sorter: sorterRooms,
        onFilter: onRoomsFilter,
        filters: rooms,
        onHeaderCell: () => ({ id: columnIds.materialRooms }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.materialRooms,
          record,
          dataIndex: "materialRooms",
        }),
      },
      {
        key: "materialSurfaces",
        title: "Surface",
        dataIndex: "materialSurfaces",
        align: "left",
        render: renderArrayStringCell,
        sorter: sorterSurfaces,
        onFilter: onSurfacesFilter,
        filters: surfaces,
        onHeaderCell: () => ({ id: columnIds.materialSurfaces }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.materialSurfaces,
          record,
          dataIndex: "materialSurfaces",
        }),
      },
      {
        key: "materialManufacturer",
        title: "Manufacturer",
        dataIndex: "materialManufacturer",
        align: "left",
        render: renderTextTooltipCell,
        sorter: sorterMaterialManufacturer,
        onHeaderCell: () => ({ id: columnIds.materialManufacturer }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.materialManufacturer,
          record,
          dataIndex: "materialManufacturer",
        }),
      },
      {
        key: "homeNames",
        title: "Homes",
        dataIndex: "homeNames",
        align: "left",
        render: renderArrayStringCell,
        sorter: sorterHomeNames,
        onHeaderCell: () => ({ id: columnIds.homeNames }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.homeNames,
          record,
          dataIndex: "homeNames",
        }),
      },
      {
        key: "updatedByFullName",
        title: "Modified By",
        dataIndex: "updatedByFullName",
        align: "left",
        render: renderTextTooltipCell,
        sorter: sorterUpdatedByFullName,
        onHeaderCell: () => ({ id: columnIds.updatedByFullName }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.updatedByFullName,
          record,
          dataIndex: "updatedByFullName",
        }),
      },
      {
        key: "updatedAt",
        title: "Date Modified",
        dataIndex: "updatedAt",
        align: "left",
        render: renderUpdatedAt,
        sorter: sorterUpdatedAt,
        onHeaderCell: () => ({ id: columnIds.updatedAt }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.updatedAt,
          record,
          dataIndex: "updatedAt",
        }),
      },
      {
        key: "isActive",
        title: "Status",
        render: renderStatusCell,
        filters: [
          {
            text: "Active",
            value: true,
          },
          {
            text: "Inactive",
            value: false,
          },
        ],
        sorter: sorterStatus,
        onFilter: onStatusFilter,
        onHeaderCell: () => ({ id: columnIds.isActive }),
        onCell: (record: GetMaterialDto) => ({
          id: columnIds.isActive,
          record,
          dataIndex: "isActive",
        }),
      },
    ],
    [
      categories,
      onCategoriesFilter,
      onRoomsFilter,
      onStatusFilter,
      onSurfacesFilter,
      renderArrayStringCell,
      renderStatusCell,
      renderTextTooltipCell,
      renderThumbnailCell,
      renderUpdatedAt,
      rooms,
      sorterCategories,
      sorterHomeNames,
      sorterMaterialKey,
      sorterMaterialManufacturer,
      sorterMaterialName,
      sorterMaterialThumbnailUrl,
      sorterRooms,
      sorterStatus,
      sorterSurfaces,
      sorterUnitPrice,
      sorterUpdatedAt,
      sorterUpdatedByFullName,
      surfaces,
    ],
  );

  const [columns, setColumns] = React.useState(() =>
    baseColumns().map((column, index) => ({
      ...column,
      onHeaderCell: () => ({ id: `${index}` }),
      onCell: () => ({ id: `${index}` }),
    })),
  );

  const setDisplayColumnVisibility = React.useCallback(
    (
      columnsVisibility: MaterialColumns,
      categoryFilters: ColumnFilterItem[],
      roomFilters: ColumnFilterItem[],
      surfaceFilters: ColumnFilterItem[],
    ) => {
      setDisplayColumns(columnsVisibility);

      const columnLabels = [];
      for (const property in columnsVisibility) {
        if (columnsVisibility[property]) {
          columnLabels.push(titleKeys[property]);
        }
      }

      const newColumnList = columns.map((column) => {
        const columnItem = { ...column };
        if (columnLabels.includes(column.title as string)) {
          columnItem.width = "auto";
        } else {
          columnItem.width = "0px";
        }

        switch (columnItem.key) {
          case "materialCategories":
            return {
              ...columnItem,
              filters: categoryFilters,
            };
          case "materialRooms":
            return {
              ...columnItem,
              filters: roomFilters,
            };
          case "materialSurfaces":
            return {
              ...columnItem,
              filters: surfaceFilters,
            };
          default:
            return columnItem;
        }
      });
      setColumns(newColumnList);
    },
    [columns],
  );

  React.useEffect(() => {
    async function asyncLoadData() {
      if (materialDispatch) {
        const materialUsecase = new MaterialUsecase(materialDispatch);
        const materialData = await materialUsecase.getList(listParams);
        const categoryFilters = getListCategoryFilters(materialData);
        const roomFilters = getListRoomFilters(materialData);
        const surfaceFilters = getListSurfaceFilters(materialData);

        const columns = materialUsecase.getDisplayColumns();
        setDisplayColumnVisibility(
          columns,
          categoryFilters,
          roomFilters,
          surfaceFilters,
        );
      }

      if (tenantDispatch) {
        const tenantUsecase = new TenantUsecase(tenantDispatch);
        await tenantUsecase.getList({});
      }
    }

    asyncLoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    materialDispatch,
    tenantDispatch,
    listParams,
    getListCategoryFilters,
    getListRoomFilters,
    getListSurfaceFilters,
  ]);

  React.useEffect(() => {
    let newColumns = [...columns];
    newColumns = newColumns.map((columnItem) => {
      switch (columnItem.key) {
        case "materialCategories":
          return {
            ...columnItem,
            filters: categories,
          };
        case "materialRooms":
          return {
            ...columnItem,
            filters: rooms,
          };
        case "materialSurfaces":
          return {
            ...columnItem,
            filters: surfaces,
          };
        default:
          return columnItem;
      }
    });
    setColumns(newColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, rooms, surfaces, materialState.data]);

  const onDisplayColumnChanged = React.useCallback(
    (list: CheckboxValueType[]) => {
      if (materialDispatch) {
        const materialUsecase = new MaterialUsecase(materialDispatch);

        const newColumns: MaterialColumns = {
          materialThumbnailUrl: false,
          materialKey: false,
          name: false,
          materialCategories: false,
          materialUnitPrice: false,
          materialRooms: false,
          materialSurfaces: false,
          materialManufacturer: false,
          homeNames: false,
          updatedByFullName: false,
          updatedAt: false,
          isActive: false,
        };
        let columnLength = 0;
        for (const property in newColumns) {
          const label = titleKeys[property] as string;
          if (list.includes(label)) {
            newColumns[property] = true;
          }
          columnLength++;
        }
        materialUsecase.setDisplayColumns(newColumns);
        setDisplayColumnVisibility(newColumns, categories, rooms, surfaces);
        setIndeterminate(!!list.length && list.length < columnLength);
        setCheckAll(list.length === columnLength);
      }
    },
    [categories, materialDispatch, rooms, setDisplayColumnVisibility, surfaces],
  );

  const [indeterminate, setIndeterminate] = React.useState(true);
  const [checkAll, setCheckAll] = React.useState(false);
  const onCheckAllChange = React.useCallback(
    (e: CheckboxChangeEvent) => {
      if (materialDispatch) {
        const newColumns: MaterialColumns = {
          materialThumbnailUrl: e.target.checked,
          materialKey: e.target.checked,
          name: e.target.checked,
          materialCategories: e.target.checked,
          materialUnitPrice: e.target.checked,
          materialRooms: e.target.checked,
          materialSurfaces: e.target.checked,
          materialManufacturer: e.target.checked,
          homeNames: e.target.checked,
          updatedByFullName: e.target.checked,
          updatedAt: e.target.checked,
          isActive: e.target.checked,
        };
        const materialUsecase = new MaterialUsecase(materialDispatch);
        materialUsecase.setDisplayColumns(newColumns);
        setDisplayColumnVisibility(newColumns, categories, rooms, surfaces);
        setIndeterminate(false);
        setCheckAll(e.target.checked);
      }
    },
    [categories, materialDispatch, rooms, setDisplayColumnVisibility, surfaces],
  );

  React.useEffect(() => {
    const listColumns = [];
    columns.forEach((column) => {
      if (column.title !== "Thumbnail") {
        listColumns.push({
          ...column,
        });
      } else {
        listColumns.push({
          ...column,
          render: renderThumbnailCell,
        });
      }
    });
    setColumns(listColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderThumbnailCell, thumbnailSize]);

  const onThumbnailSizeChange = React.useCallback((e: RadioChangeEvent) => {
    setThumbnailSize(e.target.value);
  }, []);

  React.useEffect(() => {
    const columnOnCells = {
      materialThumbnailUrl: () => ({
        id: columnIds.materialThumbnailUrl,
      }),
      materialKey: () => ({
        id: columnIds.materialKey,
      }),
      name: (record: GetMaterialDto) => ({
        id: columnIds.name,
        editing: isEditing(),
        inputType: InputType.Text,
        record,
        dataIndex: "name",
      }),
      materialCategories: (record: GetMaterialDto) => ({
        id: columnIds.materialCategories,
        editing: isEditing(),
        inputType: InputType.Text,
        record,
        dataIndex: "materialCategories",
      }),
      materialUnitPrice: (record: GetMaterialDto) => ({
        id: columnIds.materialUnitPrice,
        editing: isEditing(),
        inputType: InputType.Number,
        record,
        dataIndex: "materialUnitPrice",
      }),
      materialRooms: (record: GetMaterialDto) => ({
        id: columnIds.materialRooms,
        record,
        dataIndex: "materialRooms",
      }),
      materialSurfaces: (record: GetMaterialDto) => ({
        id: columnIds.materialSurfaces,
        record,
        dataIndex: "materialSurfaces",
      }),
      materialManufacturer: (record: GetMaterialDto) => ({
        id: columnIds.materialManufacturer,
        editing: isEditing(),
        inputType: InputType.Text,
        record,
        dataIndex: "materialManufacturer",
      }),
      updatedByFullName: (record: GetMaterialDto) => ({
        id: columnIds.updatedByFullName,
        record,
        dataIndex: "updatedByFullName",
      }),
      updatedAt: (record: GetMaterialDto) => ({
        id: columnIds.updatedAt,
        record,
        dataIndex: "updatedAt",
      }),
      isActive: (record: GetMaterialDto) => ({
        id: columnIds.isActive,
        editing: isEditing(),
        inputType: InputType.Switch,
        record,
        dataIndex: "isActive",
      }),
    };

    const newColumns = [];
    columns.forEach((column) => {
      newColumns.push({
        ...column,
        onCell: columnOnCells[column.key],
      });
    });
    setColumns(newColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const openNotification = () => {
    const key = `open${Date.now()}`;
    const btn = (
      <Space>
        <Button
          type="primary"
          size="small"
          css={Styles.notificationButonStyle}
          onClick={() => notification.close(key)}
          icon={<div className="ico-check-white" style={{ marginRight: 10 }} />}
        >
          Confirm
        </Button>
      </Space>
    );
    notification.success({
      message: "Saved successfully",
      description: "Changes have been saved",
      btn,
      key,
      onClose: close,
      icon: <div className="ico-check-blue" css={Styles.iconSuccessStyle} />,
    });
  };

  const getUpdateValues = React.useCallback(() => {
    const fieldValues = form.getFieldsValue();
    const updateKeys = [];
    editKeys.forEach((key) => {
      const item = {
        materialKey: key,
      };
      for (const property in fieldValues) {
        const dataIndexKeyPair = property.split("_");
        const dataIndex = dataIndexKeyPair[0];
        const keyValue = dataIndexKeyPair[1];
        if (keyValue === key) {
          if (dataIndex !== "materialCategories") {
            item[dataIndex] = fieldValues[property];
          } else {
            item[dataIndex] = fieldValues[property]?.split(",") ?? [];
          }
        }
      }
      updateKeys.push(item);
    });
    return updateKeys;
  }, [editKeys, form]);

  const saveData = React.useCallback(async () => {
    const updateValues = getUpdateValues();
    if (updateValues.length === 0) return;

    const userId = authState.userInfo?.username;
    if (materialDispatch) {
      const materialUsecase = new MaterialUsecase(materialDispatch);
      await materialUsecase.updateList({
        userId,
        items: updateValues,
      });
    }
    setEditKeys([]);
    setCheckedRowKeys([]);
    openNotification();
  }, [authState.userInfo?.username, getUpdateValues, materialDispatch]);

  const onButtonEditClick = React.useCallback(async () => {
    const fieldValues: unknown = {};
    switch (mode) {
      case MaterialMode.View:
        setMode(MaterialMode.Edit);

        materialState.data.forEach((item) => {
          fieldValues[`name_${item.materialKey}`] = item.name;
          fieldValues[`materialCategories_${item.materialKey}`] =
            item.materialCategories?.join(",");
          fieldValues[`materialUnitPrice_${item.materialKey}`] =
            item.materialUnitPrice;
          fieldValues[`materialManufacturer_${item.materialKey}`] =
            item.materialManufacturer;
          fieldValues[`isActive_${item.materialKey}`] = item.isActive;
        });
        form.setFieldsValue(fieldValues);
        break;
      case MaterialMode.Edit:
        await saveData();
        setMode(MaterialMode.View);
        break;
      default:
        break;
    }
  }, [form, materialState.data, mode, saveData]);

  const onButtonCancelClick = React.useCallback(async () => {
    setEditKeys([]);
    setCheckedRowKeys([]);
    setMode(MaterialMode.View);
  }, []);

  const onRowSelectionChange = React.useCallback(
    (selectedRowKeys: React.Key[]) => {
      setCheckedRowKeys(selectedRowKeys);
    },
    [],
  );

  const rowSelection =
    React.useCallback((): TableRowSelection<GetMaterialDto> => {
      if (mode === MaterialMode.Edit) {
        return {
          type: "checkbox",
          onChange: onRowSelectionChange,
        };
      }
      return null;
    }, [mode, onRowSelectionChange]);

  const onFormValueChanges = React.useCallback(
    (changesValue) => {
      const keys = editKeys;
      for (const property in changesValue) {
        const dataIndexKeyPair = property.split("_");
        const dataIndex = dataIndexKeyPair[0];
        const key = dataIndexKeyPair[1];
        if (keys.includes(key) === false) {
          keys.push(key);
        }

        if (checkedRowKeys.length > 1) {
          const fieldValues = {};
          checkedRowKeys.forEach((rowKey) => {
            if (keys.includes(rowKey) === false) {
              keys.push(rowKey);
            }
            fieldValues[`${dataIndex}_${rowKey}`] = changesValue[property];
          });
          form.setFieldsValue(fieldValues);
        }
      }
      setEditKeys(keys);
    },
    [checkedRowKeys, editKeys, form],
  );

  return (
    <LayoutContent
      title="MATERIALS"
      iconPath={Images.PALETTE_BLACK}
      toolbar={
        <Row>
          <Col span={18}>
            <Input.Search
              placeholder="search..."
              allowClear
              css={Styles.inputSearchStyle}
              style={{ width: 300, verticalAlign: "middle" }}
              size="large"
              onChange={onKeywordChangeHandle}
              enterButton
            />
            <Popover
              placement="bottomRight"
              trigger="hover"
              content={
                <>
                  <Checkbox
                    indeterminate={indeterminate}
                    onChange={onCheckAllChange}
                    checked={checkAll}
                  >
                    Select all items
                  </Checkbox>
                  <br />
                  <Checkbox.Group
                    options={displayColumnOptions()}
                    value={displayColumnValues()}
                    onChange={onDisplayColumnChanged}
                    css={Styles.checkboxGroupStyle}
                  />
                </>
              }
            >
              <Button
                size="large"
                style={{ marginLeft: 8 }}
                icon={<EyeInvisibleOutlined />}
              >
                Hide Column
              </Button>
            </Popover>
            <Popover
              placement="bottomRight"
              trigger="hover"
              content={
                <>
                  <Radio.Group
                    value={thumbnailSize}
                    onChange={onThumbnailSizeChange}
                  >
                    <Space direction="vertical">
                      <Radio value={"small"}>Small</Radio>
                      <Radio value={"medium"}>Medium</Radio>
                      <Radio value={"large"}>Large</Radio>
                    </Space>
                  </Radio.Group>
                </>
              }
            >
              <Button
                size="large"
                style={{ marginLeft: 8 }}
                icon={<FileImageOutlined />}
              >
                Thumbnail Size
              </Button>
            </Popover>
          </Col>
          <Col span={6} css={Styles.flexEndStyle}>
            <Button
              size="large"
              style={{ marginLeft: 8 }}
              onClick={onButtonEditClick}
              className={mode === MaterialMode.Edit ? "active" : ""}
              type={isEditing() ? "primary" : "default"}
              icon={isEditing() ? <SaveOutlined /> : <EditOutlined />}
            >
              {isEditing() ? "Save" : "Edit"}
            </Button>
            {isEditing() ? (
              <Button
                size="large"
                style={{ marginLeft: 8 }}
                onClick={onButtonCancelClick}
                icon={<CloseCircleOutlined />}
              >
                Cancel
              </Button>
            ) : (
              <></>
            )}
          </Col>
        </Row>
      }
    >
      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={columns.map((i) => i.key)}
          strategy={horizontalListSortingStrategy}
        >
          <DragIndexContext.Provider value={dragIndex}>
            <Form
              form={form}
              component={false}
              onValuesChange={onFormValueChanges}
            >
              <Table<GetMaterialDto>
                rowKey="materialKey"
                dataSource={materialState.data}
                css={Styles.tableStyle}
                pagination={{ position: ["topRight"], disabled: isEditing() }}
                scroll={{ y: "calc(100vh - 215px - 200px)" }}
                rowSelection={rowSelection()}
                columns={columns}
                components={{
                  header: { cell: TableHeaderCell },
                  body: { cell: TableBodyCell },
                }}
              ></Table>
            </Form>
          </DragIndexContext.Provider>
        </SortableContext>
        <DragOverlay>
          <th
            style={{
              backgroundColor: "#E7E7E7",
              padding: 16,
              width: 300,
            }}
          >
            {
              columns[columns.findIndex((i) => i.key === dragIndex.active)]
                ?.title as React.ReactNode
            }
          </th>
        </DragOverlay>
      </DndContext>
    </LayoutContent>
  );
};

export const Material = React.memo(MaterialComponent);
